import { UserProfile } from 'api/account/models/UserProfile';
import UsersService from 'api/users/UsersService';
import { VehiclesSearchCriteria } from 'api/vehicles/models/VehiclesSearchCriteria';
import { VehicleViewModel } from 'api/vehicles/models/VehicleViewModel';
import VehiclesService from 'api/vehicles/VehiclesService';
import MoneyFormat from 'common/components/moneyFormat/MoneyFormat';
import Logger from 'common/services/Logger';
import { DATE_FORMAT_DEFAUT, DefaultAssetImage, DEFAULT_PAGINATION_ITEMS_PER_PAGE, MAX_PAGINATION_ITEMS_PER_PAGE, LOGGER_LOG_TYPE, USE_CONTRACT_TYPES } from 'Config';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BiTargetLock } from 'react-icons/bi';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useToasts } from 'react-toast-notifications';
import ReactTooltip from 'react-tooltip';
import { Reducers } from 'store/types';
import styles from './VehiclesMileageScreen.module.scss';
import VehiclesMileageFiltersScreen, { Filters } from './components/VehiclesMileageFiltersScreen';
import FiltersIcon from 'assets/svg/desktop_filtres.svg';
import CarIcon from 'assets/svg/car.svg';
import GaugeIcon from 'assets/svg/gauge.svg';
import GaugeDangerIcon from 'assets/svg/gauge-danger.svg';
import MediaIcon from 'assets/svg/media.svg';
import AlertIcon from 'assets/svg/i_alertas_white.svg';
import ScreenTitle from 'common/components/screenTitle/ScreenTitle';
import DateFormat from 'common/components/dateFormat/dateFormat';
import Popover from 'common/components/popover/Popover';
import ScreenHeaderButton from 'common/components/screenHeader/ScreenHeaderButton';
import { ContractStatusColorEnum } from 'api/contracts/enums/ContractStatusColorEnum';
import { VehicleMileageCounterListDto } from 'api/vehicles/models/VehicleDto';
import Loading from 'common/services/Loading';
import { FaClock, FaHourglassEnd, FaRegClock } from 'react-icons/fa';
import TrackingService from 'api/tracking/TrackingService';
import LoadingSpinner from 'common/components/loading/LoadingSpinner';
import CompaniesService from 'api/companies/CompaniesService';
import { CompanyDto } from 'api/companies/models/CompanyDto';

import DropdownItem from 'common/components/popover/PopoverItem';
import Dropdown from 'common/components/dropdown/Dropdown';
import { FaFileExport } from 'react-icons/fa';
import moment from 'moment';
import colors from 'styles/export/colors.module.scss';
import EcoDriving from 'screens/vehicles-gps/components/ecoDriving/EcoDriving';

import FilterBadgesOverflow, { FilterBadgeItem } from 'common/components/filterBadgesOverflow/FilterBadgesOverflow';
import { CompactPageLayout, compactPageLayoutStyles } from 'common/components/compactPageLayout';
import InfoCard from 'common/components/infoCard/InfoCard';
import DataTableWithSidebar from 'common/components/dataTable/DataTableWithSidebar';
import { DataTableColumn, OrderData } from 'common/components/dataTable/models';
import useBidirectionalScroll from 'common/hooks/useBidirectionalScroll';
import IconWithText from 'common/components/iconWithText/IconWithText';
import Badge from 'common/components/badge/Badge';
import { saveAs } from 'file-saver';

const FILTERS_CACHE_KEY = 'VEHICLESMILEAGEFILTER';

const INFO_CARD_COLORS = [
    colors.counterTeal1,
    colors.counterTeal2,
    colors.counterBlue1,
];

const VehiclesMileageScreen = () => {

    const { t } = useTranslation();
    const { addToast } = useToasts();

    const [vehicles, setVehicles] = useState<VehicleViewModel[]>([]);
    const history = useHistory();
    const [totalItems, setTotalItems] = useState(0);
    const [isLoadingMore, setIsLoadingMore] = useState(false);

    const [filters, setFilters] = useState<Filters>({});
    const [filtersTotal, setFiltersTotal] = useState(0);

    const [counters, setCounters] = useState<VehicleMileageCounterListDto>();

    const imageCacheKey = useRef(new Date().getTime());
    const exportListingRef = useRef<{ exportExcel: () => Promise<void> } | null>(null);

    const userProfile = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const hasGpsKmsReadPolicy = UsersService.hasPolicies(userProfile?.policies || [], ['SETTINGUP_GPS_KMS_READ']);
    const hasExportListingPolicy = UsersService.hasPolicies(userProfile?.policies || [], ['SETTINGUP_EXPORT_LISTING_WRITE']);

    // Bidirectional scroll hook
    const bidirectionalScroll = useBidirectionalScroll({
        cacheKey: FILTERS_CACHE_KEY,
        itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
    });

    const {
        loadedPageRange,
        isLoadingPrevious,
        scrollToRowId: scrollToItemId,
        shouldLoadAdjacentPages,
        setLoadedPageRange,
        setIsLoadingPrevious,
        resetScrollState,
        calculatePageFromIndex,
        prepareLoadPrevious,
        adjustScrollAfterPrepend,
        finishRestoration,
        initiateRestoration,
        getHasMore,
        getHasMorePrevious,
        getOnLoadMore,
        getOnLoadPrevious,
    } = bidirectionalScroll;

    const countersLoaded = useRef<boolean>(false);
    const previousCriteria = useRef<VehiclesSearchCriteria | null>(null);
    const isLoadingData = useRef<boolean>(false);

    const [criteria, setCriteria] = useState<VehiclesSearchCriteria>({
        itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
        page: 1,
        orderColumn: 'v.registration_number',
        orderBy: 'asc',
        hasKmFeature: true,
    });

    const [company, setCompany] = useState<CompanyDto>();
    const iteration = useRef<number>(0);
    const isFirstLoad = useRef(true);
    const [reRenderSearch, setReRenderSearch] = useState<number>(0);

    // Order state for DataTable
    const [order, setOrder] = useState<OrderData<VehicleViewModel>>({
        field: 'registrationNumber',
        orderColumn: 'v.registration_number',
        isOrderAsc: true,
    });

    const getFieldFromOrderColumn = (oc: string): keyof VehicleViewModel => {
        const mapping: Record<string, string> = {
            'v.registration_number': 'registrationNumber',
            'v.active': 'active',
            'vb.name': 'vehicleBrandName',
            'vm.name': 'vehicleModelName',
            'use_contract_info.name': 'useContractTypeName',
            'expired_use_contract_info.end_date': 'useContractExpiredEndDate',
            'use_contract_info.number': 'useContractNumber',
            'use_contract_info.start_date': 'useContractStartDate',
            'use_contract_info.end_date': 'useContractEndDate',
            'use_contract_info.past_months': 'useContractPastMonths',
            'use_contract_info.km_contractual': 'useContractKmContractual',
            'use_contract_info.kms_by_month': 'useContractKmsByMonth',
            'v.current_kilometers': 'currentKilometers',
            'use_contract_kms_real': 'useContractKmsReal',
            'projection': 'projection',
            'projection_exceeded': 'projectionExceeded',
            'cost_exceeded_total': 'useContractCostPerKmOver',
            'projection_under': 'projectionExceeded',
            'cost_under_total': 'useContractCostPerKmUnder',
        };
        return (mapping[oc] ?? 'registrationNumber') as keyof VehicleViewModel;
    };

    const getData = async (isLoadMore = false, shouldUpdateCounters = false) => {
        // Prevent concurrent API calls
        if (isLoadingData.current) {
            return;
        }
        isLoadingData.current = true;

        try {
            if (!isLoadMore) {
                Loading.show();
            } else {
                setIsLoadingMore(true);
            }

            saveOrdernationFilters();

            let countersDB = null;
            if (shouldUpdateCounters) {
                countersDB = await VehiclesService.getMileageCountersList(criteria);
            }

            const [vehiclesDB, c] = await Promise.all([
                VehiclesService.getList(criteria),
                CompaniesService.getCurrent()
            ]);

            setTotalItems(vehiclesDB.totalItems);
            setCompany(c);

            if (isLoadMore) {
                setVehicles(prev => [...prev, ...vehiclesDB.items]);
                setLoadedPageRange(prev => ({ ...prev, max: criteria.page }));
            } else {
                setVehicles(vehiclesDB.items);
            }

            if (countersDB) {
                setCounters(countersDB);
                countersLoaded.current = true;
            }

            // Defer tooltip rebuild to after render cycle
            setTimeout(() => ReactTooltip.rebuild(), 0);

            // Handle scroll restoration
            if (shouldLoadAdjacentPages.current) {
                shouldLoadAdjacentPages.current = false;
                const currentPage = criteria.page;
                const totalPages = Math.ceil(vehiclesDB.totalItems / (criteria.itemsPerPage || DEFAULT_PAGINATION_ITEMS_PER_PAGE));

                // Load previous page if exists
                if (currentPage > 1) {
                    try {
                        const prevCriteria = { ...criteria, page: currentPage - 1 };
                        const prevResult = await VehiclesService.getList(prevCriteria);
                        setVehicles(prev => [...prevResult.items, ...prev]);
                        setLoadedPageRange(prev => ({ ...prev, min: currentPage - 1 }));
                    } catch (e) {
                        Logger.error(LOGGER_LOG_TYPE.VEHICLES, `Couldn't load previous page`, e);
                    }
                }

                // Load next page if exists
                if (currentPage < totalPages) {
                    try {
                        const nextCriteria = { ...criteria, page: currentPage + 1 };
                        const nextResult = await VehiclesService.getList(nextCriteria);
                        setVehicles(prev => [...prev, ...nextResult.items]);
                        setLoadedPageRange(prev => ({ ...prev, max: currentPage + 1 }));
                    } catch (e) {
                        Logger.error(LOGGER_LOG_TYPE.VEHICLES, `Couldn't load next page`, e);
                    }
                }

                finishRestoration();
            }

        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.VEHICLES, `Couldn't get vehicles list`, error);
            addToast(t('common.messages.error_load_info'), { appearance: 'error' });
            resetScrollState();
        } finally {
            Loading.hide();
            setIsLoadingMore(false);
            isLoadingData.current = false;
        }
    };

    const hydrateGpsData = async () => {
        try {
            iteration.current++;
            const it = iteration.current;
            // Only hydrate vehicles that have a tracking device AND haven't been hydrated yet
            const vehiclesToHydrate = vehicles.filter(x => x.trackingDeviceId && !x.gpsDataHydrated);
            if (!vehiclesToHydrate.length) {
                return;
            }

            const gpsData = await TrackingService.hydrateGpsData({
                remoteVehicleIds: vehiclesToHydrate.map(x => x.trackingDeviceId),
                startDate: criteria.pathStartDate,
                endDate: criteria.pathEndDate,
                page: 1,
                itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE
            });


            const _vehicles = vehicles.map(vehicle => {
                const gps = gpsData.find(x => x.remoteVehicleId == vehicle.trackingDeviceId);
                if (gps) {
                    return { ...vehicle, totalKms: gps.totalKms, totalPenalty: gps.totalPenalty, totalMinutes: gps.totalMinutes, totalIdling: gps.totalIdling, totalDowntime: gps.totalDowntime, gpsDataHydrated: true }
                }
                return { ...vehicle, gpsDataHydrated: true };
            });
            if (iteration.current == it) {
                setVehicles(_vehicles);
            }


        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.VEHICLES, `Couldn't get vehicles gps gata`, error);
            addToast(t('common.messages.error_load_info'), { appearance: 'error' });
        }
    }

    // Handler for infinite scroll load more
    const loadMore = useCallback(() => {
        // Use ref for synchronous check to prevent multiple rapid calls
        if (isLoadingData.current || isLoadingMore || vehicles.length >= totalItems) return;
        const nextPage = loadedPageRange.max + 1;
        setCriteria(prev => ({ ...prev, page: nextPage }));
    }, [isLoadingMore, vehicles.length, totalItems, loadedPageRange.max]);

    // Load previous pages (bidirectional scroll)
    const loadPrevious = useCallback(async () => {
        if (isLoadingPrevious || loadedPageRange.min <= 1) return;

        setIsLoadingPrevious(true);
        const prevPage = loadedPageRange.min - 1;

        try {
            prepareLoadPrevious();
            const prevCriteria: VehiclesSearchCriteria = {
                ...criteria,
                page: prevPage,
                itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
            };
            const result = await VehiclesService.getList(prevCriteria);

            setVehicles(prev => [...result.items, ...prev]);
            setLoadedPageRange(prev => ({ ...prev, min: prevPage }));
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.VEHICLES, `Couldn't get previous vehicles page`, error);
            addToast(t('common.messages.error_load_info'), { appearance: 'error' });
        } finally {
            setIsLoadingPrevious(false);
        }
    }, [isLoadingPrevious, loadedPageRange.min, criteria, addToast, t]);

    // Adjust scroll position after prepending items
    useEffect(() => {
        if (!isLoadingPrevious) {
            adjustScrollAfterPrepend();
        }
    }, [vehicles, isLoadingPrevious, adjustScrollAfterPrepend]);

    // Handler de ordenação
    const handleOrderChange = (orderData: OrderData<VehicleViewModel>) => {
        setOrder(orderData);
        setVehicles([]);
        resetScrollState();
        setTotalItems(0);
        setCriteria(prev => ({
            ...prev,
            page: 1,
            orderBy: orderData.isOrderAsc ? 'asc' : 'desc',
            orderColumn: orderData.orderColumn || orderData.field as string,
        }));
    };

    const formatTotalMinutes = (time: number) => {
        const diffInTime = time * 60;
        const hours = Math.floor(diffInTime / (3600))
        const minutes = Math.floor((diffInTime - (hours * 3600)) / 60);
        const seconds = Math.round((diffInTime - (hours * 3600) - (minutes * 60)));
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}  h`;
    }

    const calcPenaltyTotal = (totalKms: number, totalPenalty: number) => {
        return totalKms > 0 ? (totalPenalty / totalKms) : totalPenalty;
    }

    const formatNumber = (num: number, fixedDecimal: number | null = null) => {
        if (isNaN(num)) return '';

        let numberStr;

        if (fixedDecimal) {
            numberStr = Number(num).toFixed(fixedDecimal);
        } else {
            numberStr = String(num);
        }

        const parts = numberStr.split('.');
        const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
        const decimalPart = parts[1] ? ',' + parts[1] : '';

        return integerPart + decimalPart;
    }

    const renderImageCell = (row: VehicleViewModel) => {
        return (
            <div className={styles.imageDescCell}>
                <div className={styles.contentImage} style={{ width: '5rem', height: '3rem' }}>
                    <div className={styles.img} style={{ width: '5rem', height: '3rem', backgroundImage: row?.photoUrl ? 'url(' + row?.photoUrl + '?_=' + imageCacheKey.current + ')' : row?.vehicleSegmentDefaultPhotoUrl ? 'url(' + row?.vehicleSegmentDefaultPhotoUrl + '?_=' + imageCacheKey.current + ')' : 'url(' + DefaultAssetImage + ')' }} />
                </div>
            </div>
        )
    }

    const tableColumns: DataTableColumn<VehicleViewModel>[] = useMemo(() => {
        const cols: DataTableColumn<VehicleViewModel>[] = [
            // Imagem
            {
                field: 'photoUrl' as keyof VehicleViewModel,
                name: t('common.image') as string,
                hideOrderButton: true,
                width: 90,
                excludeFromExport: true,
                renderCell: renderImageCell,
            },
            // Matrícula
            {
                field: 'registrationNumber' as keyof VehicleViewModel,
                name: t('vehicles_mileage.list.vehicle') as string,
                orderColumn: 'v.registration_number',
                width: 130,
                renderCell: row => (
                    <div className={styles.descCell}>
                        <div className={styles.columnPlate}>
                            {row.registrationNumber}
                        </div>
                    </div>
                ),
            },
            // Estado do Ativo
            {
                field: 'active' as keyof VehicleViewModel,
                name: t('vehicles_mileage.list.asset_status') as string,
                orderColumn: 'v.active',
                width: 100,
                exportValue: row => row.active
                    ? t('vehicles_mileage.list.asset_active') as string
                    : t('vehicles_mileage.list.asset_inactive') as string,
                renderCell: row => (
                    <Badge backgroundColor={row.active ? '#56CF76' : '#DC4C4C'}>
                        {row.active
                            ? t('vehicles_mileage.list.asset_active')
                            : t('vehicles_mileage.list.asset_inactive')}
                    </Badge>
                ),
            },
            // Marca
            {
                field: 'vehicleBrandName' as keyof VehicleViewModel,
                name: t('vehicle.brand') as string,
                orderColumn: 'vb.name',
                width: 100,
                renderCell: row => <>{row.vehicleBrandName || '-'}</>,
            },
            // Modelo
            {
                field: 'vehicleModelName' as keyof VehicleViewModel,
                name: t('vehicle.model') as string,
                orderColumn: 'vm.name',
                width: 100,
                renderCell: row => <>{row.vehicleModelName || '-'}</>,
            },
            // Segmento
            {
                field: 'vehicleSegmentName' as keyof VehicleViewModel,
                name: t('vehicle.type') as string,
                orderColumn: 'vs.name',
                width: 130,
                renderCell: row => <>{row.vehicleSegmentName || '-'}</>,
            },
            // Agência
            {
                field: 'vehicleLocals' as keyof VehicleViewModel,
                name: t('vehicle.local') as string,
                hideOrderButton: true,
                width: 130,
                exportValue: row => (row.vehicleLocals ?? []).map(l => l.localName).join(', ') || '-',
                renderCell: row => <>{(row.vehicleLocals ?? []).map(l => l.localName).join(', ') || '-'}</>,
            },
            // Tipo Financiamento
            {
                field: 'useContractTypeName' as keyof VehicleViewModel,
                name: t('common.type_funding') as string,
                orderColumn: 'use_contract_info.name',
                width: 100,
                exportValue: row => row.useContractTypeName
                    ? t(`common.use_contract_types.${row.useContractTypeName}` as any) as string
                    : '',
                renderCell: row => {
                    if (!row.useContractTypeName) return <>-</>;
                    const label = t(`common.use_contract_types.${row.useContractTypeName}` as any);
                    if (row.useContractTypeName === 'PURCHASE' && row.useContractStartDate) {
                        const formattedDate = dayjs(row.useContractStartDate).format(DATE_FORMAT_DEFAUT);
                        return (
                            <span
                                data-tip={t('vehicles_mileage.list.purchase_date', { date: formattedDate })}
                                style={{ cursor: 'pointer' }}
                            >
                                {label}
                            </span>
                        );
                    }
                    return <>{label}</>;
                },
            },
            // Último contrato expirado em
            {
                field: 'useContractExpiredEndDate' as keyof VehicleViewModel,
                name: t('vehicles_mileage.last_contract_in') as string,
                orderColumn: 'expired_use_contract_info.end_date',
                width: 130,
                renderCell: row => {
                    // Only show if there's no active contract but there's an expired one
                    if (row.useContractTypeName || !row.useContractExpiredEndDate) return <>-</>;
                    return (
                        <span style={{ color: ContractStatusColorEnum.EXPIRED }}>
                            <DateFormat value={row.useContractExpiredEndDate} format={DATE_FORMAT_DEFAUT} />
                        </span>
                    );
                },
            },
            // Nº Contrato
            {
                field: 'useContractNumber' as keyof VehicleViewModel,
                name: t('vehicles_mileage.list.contract_number') as string,
                orderColumn: 'use_contract_info.number',
                width: 100,
                renderCell: row => <>{row.useContractNumber || '-'}</>,
            },
            // Data Início
            {
                field: 'useContractStartDate' as keyof VehicleViewModel,
                name: t('vehicles_mileage.list.contract_start_date') as string,
                orderColumn: 'use_contract_info.start_date',
                width: 110,
                renderCell: row => {
                    if (row.useContractTypeName === 'PURCHASE') return <>-</>;
                    if (!row.useContractStartDate) return <>-</>;
                    return <DateFormat value={row.useContractStartDate} format={DATE_FORMAT_DEFAUT} />;
                },
            },
            // Data Fim
            {
                field: 'useContractEndDate' as keyof VehicleViewModel,
                name: t('vehicles_mileage.list.contract_end_date') as string,
                orderColumn: 'use_contract_info.end_date',
                width: 110,
                renderCell: row => {
                    if (!row.useContractTypeName || row.useContractTypeName === 'PURCHASE') return <>-</>;
                    if (!row.useContractEndDate) return <>-</>;
                    return (
                        <DateFormat value={row.useContractEndDate} format={DATE_FORMAT_DEFAUT} />
                    );
                },
            },
            // Progresso (meses)
            {
                field: 'useContractPastMonths' as keyof VehicleViewModel,
                name: t('vehicles_mileage.list.progress_months') as string,
                orderColumn: 'use_contract_info.past_months',
                width: 110,
                exportValue: row => {
                    if (!row.useContractMonthsContract || !row.useContractPastMonths) return '';
                    return `${row.useContractPastMonths} / ${row.useContractMonthsContract}`;
                },
                renderCell: row => {
                    if (!row.useContractMonthsContract || !row.useContractPastMonths) return <>-</>;
                    const percentage = ((row.useContractPastMonths / row.useContractMonthsContract) * 100).toFixed(1);
                    return (
                        <span
                            data-tip={t('vehicles_mileage.list.progress_percentage', { percentage })}
                            style={{ cursor: 'pointer' }}
                        >
                            {`${row.useContractPastMonths} ${t('vehicles_mileage.list.of')} ${row.useContractMonthsContract}`}
                        </span>
                    );
                },
            },
            // Kms Contratuais
            {
                field: 'useContractKmContractual' as keyof VehicleViewModel,
                name: t('vehicles_mileage.list.contractual_kms') as string,
                orderColumn: 'use_contract_info.km_contractual',
                width: 140,
                headerNote: `${t('common.unit')}: km`,
                exportValue: row => {
                    if (!row.useContractTypeName) return '';
                    if (!row.useContractKmContractual) return t('vehicles_mileage.without_km_contracted') as string;
                    return row.useContractKmContractual;
                },
                renderCell: row => {
                    // If no contract, show "-"
                    if (!row.useContractTypeName) return <>-</>;
                    // If contract exists but no kms contracted, show message
                    if (!row.useContractKmContractual) {
                        return <span style={{ color: ContractStatusColorEnum.EXPIRED }}>{t('vehicles_mileage.without_km_contracted')}</span>;
                    }
                    return <MoneyFormat value={row.useContractKmContractual} suffix={' km'} fixedDecimalScale={false} />;
                },
            },
            // Média de kms mensal
            {
                field: 'useContractKmsByMonth' as keyof VehicleViewModel,
                name: t('vehicles_mileage.list.contractual_avg') as string,
                orderColumn: 'use_contract_info.kms_by_month',
                width: 120,
                headerNote: `${t('common.unit')}: kms`,
                exportValue: row => row.useContractKmsByMonth ?? '',
                renderCell: row => {
                    if (!row.useContractKmsByMonth) return <>-</>;
                    const tooltipAvg = String(t('vehicles_mileage.list.avg_kms_contract_values', {
                        monthly_average_kms: formatNumber(row.useContractKmsByMonth ?? 0),
                        contract_kms: formatNumber(row.useContractKmContractual ?? 0),
                        contract_months: String(row.useContractMonthsContract ?? 0),
                    }));
                    return (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                            <BiTargetLock />
                            <span
                                data-tip={tooltipAvg}
                                data-for="mileage-tooltip"
                                style={{ cursor: 'pointer' }}
                            >
                                <MoneyFormat value={row.useContractKmsByMonth} suffix={' kms'} fixedDecimalScale={false} />
                            </span>
                        </div>
                    );
                },
            },
            // Kms Atuais
            {
                field: 'currentKilometers' as keyof VehicleViewModel,
                name: t('vehicles_mileage.list.current_kms') as string,
                orderColumn: 'v.current_kilometers',
                width: 140,
                headerNote: `${t('common.unit')}: ${t('vehicles_mileage.list.kilometers_minified')}`,
                renderCell: row => (
                    <IconWithText
                        icon={GaugeIcon}
                        value={row.currentKilometers}
                        suffix={t('vehicles_mileage.list.kilometers_minified')}
                        isNumeric={true}
                    />
                ),
            },
            // Kms Reais (média real km/mês)
            {
                field: 'useContractKmsReal' as keyof VehicleViewModel,
                name: t('vehicles_mileage.list.real_kms') as string,
                orderColumn: 'use_contract_kms_real',
                width: 140,
                headerNote: `${t('common.unit')}: kms`,
                exportValue: row => row.useContractKmsReal !== undefined && row.useContractKmsReal !== null
                    ? Math.round(row.useContractKmsReal * 1000) / 1000
                    : '',
                renderCell: row => {
                    const avgLabel = String(t('vehicles_mileage.list.current_average'));
                    const tooltipRealKms = avgLabel + ': ' + formatNumber(row.useContractKmsReal ?? 0, 3) + ' kms<br/>(' + formatNumber(row.currentKilometers ?? 0) + ' kms atuais - ' + formatNumber(row.useContractKmVehicle ?? 0) + ' kms início) / ' + String(row.useContractPastMonths ?? 0) + ' meses';
                    return (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                            <BiTargetLock />
                            {row.useContractKmsReal !== undefined && row.useContractKmsReal !== null
                                ? <span
                                    data-tip={tooltipRealKms}
                                    data-for="mileage-tooltip"
                                    style={{ cursor: 'pointer' }}
                                  >
                                    <MoneyFormat value={row.useContractKmsReal} suffix={' kms'} decimalScale={3} fixedDecimalScale={false} />
                                  </span>
                                : '-'}
                        </div>
                    );
                },
            },
            // Projecção de kms no fim do contrato
            {
                field: 'projection' as keyof VehicleViewModel,
                name: t('vehicles_mileage.list.projection_kms_end') as string,
                orderColumn: 'projection',
                width: 140,
                headerNote: `${t('common.unit')}: kms`,
                exportValue: row => {
                    if (!row.useContractId || !row.useContractKmContractual ||
                        row.useContractTypeName === USE_CONTRACT_TYPES.PURCHASE) return '';
                    return row.projection ?? '';
                },
                renderCell: row => {
                    // Não mostrar para PURCHASE ou sem contrato/kms contratuais
                    if (!row.useContractId || !row.useContractKmContractual ||
                        row.useContractTypeName === USE_CONTRACT_TYPES.PURCHASE) {
                        return <>-</>;
                    }
                    if (!row.projection) return <>-</>;
                    const remainingMonths = (row.useContractMonthsContract ?? 0) - (row.useContractPastMonths ?? 0);
                    const projLabel = String(t('vehicles_mileage.list.projection_kms_end'));
                    const tooltipProjection = projLabel + ': ' + formatNumber(row.projection ?? 0) + ' kms<br/>' + formatNumber(row.currentKilometers ?? 0) + ' kms atuais + (' + formatNumber(row.useContractKmsReal ?? 0, 3) + ' kms/mês x ' + String(remainingMonths) + ' meses)';
                    return (
                        <span
                            data-tip={tooltipProjection}
                            data-for="mileage-tooltip"
                            style={{ cursor: 'pointer' }}
                        >
                            <IconWithText icon={GaugeIcon} value={row.projection} suffix=" kms" isNumeric={true} />
                        </span>
                    );
                },
            },
            // Kms excedidos
            {
                id: 'kmsExceeded',
                field: 'projectionExceeded' as keyof VehicleViewModel,
                name: t('vehicles_mileage.list.kms_exceeded_col') as string,
                orderColumn: 'projection_exceeded',
                width: 120,
                headerNote: `${t('common.unit')}: kms`,
                exportValue: row => {
                    if (!row.useContractId || !row.useContractKmContractual ||
                        row.useContractTypeName === USE_CONTRACT_TYPES.PURCHASE) return '';
                    if (!row.projectionExceeded || row.projectionExceeded <= 0) return '';
                    return row.projectionExceeded;
                },
                renderCell: row => {
                    if (!row.useContractId || !row.useContractKmContractual ||
                        row.useContractTypeName === USE_CONTRACT_TYPES.PURCHASE) {
                        return <>-</>;
                    }
                    // Só mostrar se projectionExceeded > 0 (excedido)
                    if (!row.projectionExceeded || row.projectionExceeded <= 0) return <>-</>;

                    const hasOverCost = row.useContractCostPerKmOver && row.useContractCostPerKmOver > 0;
                    const projLabel = String(t('vehicles_mileage.list.projection_kms_end'));
                    const tooltipExceeded = formatNumber(row.projectionExceeded ?? 0) + ' kms excedidos<br/>' + projLabel + ': ' + formatNumber(row.projection ?? 0) + ' kms - ' + formatNumber(row.useContractKmVehicle ?? 0) + ' kms no início - ' + formatNumber(row.useContractKmContractual ?? 0) + ' kms contratados';
                    return (
                        <span
                            data-tip={tooltipExceeded}
                            data-for="mileage-tooltip"
                            style={{ cursor: 'pointer', color: hasOverCost ? ContractStatusColorEnum.EXPIRED : 'inherit' }}
                        >
                            <IconWithText
                                icon={hasOverCost ? GaugeDangerIcon : GaugeIcon}
                                value={row.projectionExceeded}
                                suffix=" kms"
                                isNumeric={true}
                            />
                        </span>
                    );
                },
            },
            // Custo kms excedidos
            {
                field: 'useContractCostPerKmOver' as keyof VehicleViewModel,
                name: t('vehicles_mileage.list.cost_exceeded_col') as string,
                orderColumn: 'cost_exceeded_total',
                width: 130,
                headerNote: `${t('common.unit')}: €`,
                exportValue: row => {
                    if (!row.useContractId || !row.useContractKmContractual ||
                        row.useContractTypeName === USE_CONTRACT_TYPES.PURCHASE) return '';
                    if (!row.projectionExceeded || row.projectionExceeded <= 0 ||
                        !row.useContractCostPerKmOver || row.useContractCostPerKmOver <= 0) return '';
                    return Math.abs(Math.round((row.projectionExceeded * row.useContractCostPerKmOver) * 100) / 100);
                },
                renderCell: row => {
                    if (!row.useContractId || !row.useContractKmContractual ||
                        row.useContractTypeName === USE_CONTRACT_TYPES.PURCHASE) {
                        return <>-</>;
                    }
                    // Só mostrar se há kms excedidos E custo por km definido
                    if (!row.projectionExceeded || row.projectionExceeded <= 0 ||
                        !row.useContractCostPerKmOver || row.useContractCostPerKmOver <= 0) {
                        return <>-</>;
                    }

                    const cost = Math.abs(Math.round((row.projectionExceeded * row.useContractCostPerKmOver) * 100) / 100);
                    const costLabel = String(t('vehicles_mileage.list.cost_exceeded_col'));
                    const tooltipCostOver = costLabel + ': ' + formatNumber(cost, 2) + '€<br/>' + formatNumber(row.projectionExceeded ?? 0) + ' kms x ' + formatNumber(row.useContractCostPerKmOver ?? 0, 2) + '€/km';

                    return (
                        <span
                            data-tip={tooltipCostOver}
                            data-for="mileage-tooltip"
                            style={{ cursor: 'pointer', color: ContractStatusColorEnum.EXPIRED }}
                        >
                            <MoneyFormat value={cost} suffix="€" decimalScale={2} fixedDecimalScale={true} />
                        </span>
                    );
                },
            },
            // Kms a menos
            {
                id: 'kmsUnder',
                field: 'projectionExceeded' as keyof VehicleViewModel,
                name: t('vehicles_mileage.list.kms_under_col') as string,
                orderColumn: 'projection_under',
                width: 120,
                headerNote: `${t('common.unit')}: kms`,
                exportValue: row => {
                    if (!row.useContractId || !row.useContractKmContractual ||
                        row.useContractTypeName === USE_CONTRACT_TYPES.PURCHASE) return '';
                    if (!row.projectionExceeded || row.projectionExceeded >= 0) return '';
                    return Math.abs(row.projectionExceeded);
                },
                renderCell: row => {
                    if (!row.useContractId || !row.useContractKmContractual ||
                        row.useContractTypeName === USE_CONTRACT_TYPES.PURCHASE) {
                        return <>-</>;
                    }
                    // Só mostrar se projectionExceeded < 0 (a menos)
                    if (!row.projectionExceeded || row.projectionExceeded >= 0) return <>-</>;

                    const kmsUnder = Math.abs(row.projectionExceeded);
                    const hasUnderCost = row.useContractCostPerKmUnder && row.useContractCostPerKmUnder > 0;
                    const projLabel = String(t('vehicles_mileage.list.projection_kms_end'));
                    const tooltipUnder = formatNumber(kmsUnder) + ' kms a menos<br/>' + projLabel + ': ' + formatNumber(row.projection ?? 0) + ' kms - ' + formatNumber(row.useContractKmVehicle ?? 0) + ' kms início - ' + formatNumber(row.useContractKmContractual ?? 0) + ' kms contratados';

                    return (
                        <span
                            data-tip={tooltipUnder}
                            data-for="mileage-tooltip"
                            style={{ cursor: 'pointer', color: hasUnderCost ? ContractStatusColorEnum.EXPIRED : 'inherit' }}
                        >
                            <IconWithText
                                icon={hasUnderCost ? GaugeDangerIcon : GaugeIcon}
                                value={kmsUnder}
                                suffix=" kms"
                                isNumeric={true}
                            />
                        </span>
                    );
                },
            },
            // Custo kms a menos
            {
                field: 'useContractCostPerKmUnder' as keyof VehicleViewModel,
                name: t('vehicles_mileage.list.cost_under_col') as string,
                orderColumn: 'cost_under_total',
                width: 130,
                headerNote: `${t('common.unit')}: €`,
                exportValue: row => {
                    if (!row.useContractId || !row.useContractKmContractual ||
                        row.useContractTypeName === USE_CONTRACT_TYPES.PURCHASE) return '';
                    if (!row.projectionExceeded || row.projectionExceeded >= 0 ||
                        !row.useContractCostPerKmUnder || row.useContractCostPerKmUnder <= 0) return '';
                    const kmsUnder = Math.abs(row.projectionExceeded);
                    return Math.abs(Math.round((kmsUnder * row.useContractCostPerKmUnder) * 100) / 100);
                },
                renderCell: row => {
                    if (!row.useContractId || !row.useContractKmContractual ||
                        row.useContractTypeName === USE_CONTRACT_TYPES.PURCHASE) {
                        return <>-</>;
                    }
                    // Só mostrar se há kms a menos E custo por km definido
                    if (!row.projectionExceeded || row.projectionExceeded >= 0 ||
                        !row.useContractCostPerKmUnder || row.useContractCostPerKmUnder <= 0) {
                        return <>-</>;
                    }

                    const kmsUnder = Math.abs(row.projectionExceeded);
                    const cost = Math.abs(Math.round((kmsUnder * row.useContractCostPerKmUnder) * 100) / 100);
                    const costLabel = String(t('vehicles_mileage.list.cost_under_col'));
                    const tooltipCostUnder = costLabel + ': ' + formatNumber(cost, 2) + '€<br/>' + formatNumber(kmsUnder) + ' kms x ' + formatNumber(row.useContractCostPerKmUnder ?? 0, 2) + '€/km';

                    return (
                        <span
                            data-tip={tooltipCostUnder}
                            data-for="mileage-tooltip"
                            style={{ cursor: 'pointer', color: ContractStatusColorEnum.EXPIRED }}
                        >
                            <MoneyFormat value={cost} suffix="€" decimalScale={2} fixedDecimalScale={true} />
                        </span>
                    );
                },
            },
        ];

        // GPS columns (only if user has policy)
        if (hasGpsKmsReadPolicy) {
            // Distância GPS
            cols.push({
                field: 'totalKms' as keyof VehicleViewModel,
                name: t('vehicles_mileage.list.gps_distance') as string,
                width: 115,
                hideOrderButton: true,
                excludeFromExport: true,
                renderCell: row => {
                    if (row.trackingDeviceId && !row.gpsDataHydrated) {
                        return <LoadingSpinner />;
                    }
                    return row.totalKms
                        ? <IconWithText icon={GaugeIcon} value={row.totalKms} suffix={' km'} isNumeric={true} decimalScale={3} />
                        : <>-</>;
                },
            });

            // Tempo GPS
            cols.push({
                field: 'totalMinutes' as keyof VehicleViewModel,
                name: t('vehicles_mileage.list.gps_time') as string,
                width: 115,
                hideOrderButton: true,
                excludeFromExport: true,
                renderCell: row => {
                    if (row.trackingDeviceId && !row.gpsDataHydrated) {
                        return <LoadingSpinner />;
                    }
                    if (!row.totalMinutes) return <>-</>;
                    return <><FaRegClock /> {formatTotalMinutes(row.totalMinutes)}</>;
                },
            });

            // Tempo Ralenti
            cols.push({
                field: 'totalIdling' as keyof VehicleViewModel,
                name: t('vehicles_mileage.list.gps_idling') as string,
                width: 115,
                hideOrderButton: true,
                excludeFromExport: true,
                renderCell: row => {
                    if (row.trackingDeviceId && !row.gpsDataHydrated) {
                        return <LoadingSpinner />;
                    }
                    if (!row.totalIdling) return <>-</>;
                    return <><FaHourglassEnd /> {formatTotalMinutes(row.totalIdling / 60)}</>;
                },
            });

            // Tempo Parado
            cols.push({
                field: 'totalDowntime' as keyof VehicleViewModel,
                name: t('vehicles_mileage.list.gps_downtime') as string,
                width: 115,
                hideOrderButton: true,
                excludeFromExport: true,
                renderCell: row => {
                    if (row.trackingDeviceId && !row.gpsDataHydrated) {
                        return <LoadingSpinner />;
                    }
                    if (!row.totalDowntime) return <>-</>;
                    return <><FaClock /> {formatTotalMinutes(row.totalDowntime)}</>;
                },
            });

            // Eco-Driving
            cols.push({
                field: 'totalPenalty' as keyof VehicleViewModel,
                name: t('vehicles_mileage.list.gps_eco_driving') as string,
                width: 100,
                hideOrderButton: true,
                excludeFromExport: true,
                renderCell: row => {
                    if (row.trackingDeviceId && !row.gpsDataHydrated) {
                        return <LoadingSpinner />;
                    }
                    const ecoValue = row.totalKms && row.totalPenalty ? calcPenaltyTotal(row.totalKms, row.totalPenalty) : 0;
                    if (!ecoValue && !row.totalKms) return <>-</>;
                    return <EcoDriving value={ecoValue} idx={row.id} />;
                },
            });
        }

        return cols;
    }, [t, hasGpsKmsReadPolicy]);

    const navigateTo = (type?: string, id?: string) => {
        if (id) {
            history.push(`/vehicles/${type}/${id}`);
        } else {
            history.push(`/vehicles/${type}`);
        }
    }

    const onRowClick = (row: VehicleViewModel, index: number, event: any) => {
        if (row.id) {
            event.preventDefault();
            event.stopPropagation();

            // Calculate the actual page of the clicked item based on its position in the list
            const actualPage = calculatePageFromIndex(index);

            // Save scroll position with actual page and item id
            const newFilters = { ...filters };
            newFilters.page = actualPage;
            newFilters.scrollToItemId = row.id;
            newFilters.orderBy = criteria.orderBy;
            newFilters.orderColumn = criteria.orderColumn;
            saveFiltersOnCache(newFilters);

            if (event.ctrlKey) {
                window.open(`vehicles/details/${row.id}`, '_blank');
                return;
            }
            navigateTo('details', row.id);
        }
    };

    const saveOrdernationFilters = () => {
        const newFilters = { ...filters };
        newFilters.orderBy = criteria.orderBy;
        newFilters.orderColumn = criteria.orderColumn;
        saveFiltersOnCache(newFilters);
    };

    const saveFiltersOnCache = (f: Filters) => {
        setFilters(f);
        if (f.scrollToItemId && f.page) {
            localStorage.setItem(FILTERS_CACHE_KEY, JSON.stringify(f));
        } else {
            const { page, scrollToItemId, ...filtersWithoutScrollData } = f as Filters & { scrollToItemId?: string };
            localStorage.setItem(FILTERS_CACHE_KEY, JSON.stringify(filtersWithoutScrollData));
        }
    };

    useEffect(() => {
        if (isFirstLoad.current === true) {
            loadFilters();
        } else {
            // Detect if it's a load more (page > max loaded page and items already exist)
            const isLoadMore = criteria.page > loadedPageRange.max && vehicles.length > 0;

            // Check if only order/page changed (not filters that affect counters)
            const prev = previousCriteria.current;
            const onlyOrderOrPageChanged = prev &&
                prev.registrationNumber === criteria.registrationNumber &&
                prev.segmentId === criteria.segmentId &&
                JSON.stringify(prev.localIds) === JSON.stringify(criteria.localIds) &&
                prev.localOperator === criteria.localOperator &&
                prev.driverId === criteria.driverId &&
                prev.useContractTypeId === criteria.useContractTypeId &&
                prev.useContractActive === criteria.useContractActive &&
                prev.useContractEndsAfter === criteria.useContractEndsAfter &&
                prev.useContractEndsBefore === criteria.useContractEndsBefore &&
                prev.inactives === criteria.inactives &&
                prev.pathStartDate === criteria.pathStartDate &&
                prev.pathEndDate === criteria.pathEndDate;

            // Always load counters on first data fetch, or when filters changed
            const shouldLoadCounters = !countersLoaded.current || !onlyOrderOrPageChanged;
            getData(isLoadMore, shouldLoadCounters);
            previousCriteria.current = { ...criteria };
        }
    }, [criteria]);

    useEffect(() => {
        // Check if ANY vehicle needs GPS data hydration (not just the first one)
        const hasUnhydratedVehicles = vehicles.some(v => v.trackingDeviceId && !v.gpsDataHydrated);
        if (hasUnhydratedVehicles && vehicles.length > 0) {
            hydrateGpsData();
        }
    }, [vehicles]);

    const loadFilters = () => {
        // Set isFirstLoad to false BEFORE any setCriteria to avoid duplicate calls
        isFirstLoad.current = false;

        const cachedFilters = localStorage.getItem(FILTERS_CACHE_KEY);
        if (cachedFilters != null) {
            const f = JSON.parse(cachedFilters);

            // If returning to list with saved scroll position
            if (f.scrollToItemId) {
                const startPage = initiateRestoration({
                    page: f.page || 1,
                    scrollToItemId: f.scrollToItemId,
                });

                // Clear scroll position from cache
                const cleanedFilters = { ...f, scrollToItemId: undefined, page: 1 };
                localStorage.setItem(FILTERS_CACHE_KEY, JSON.stringify(cleanedFilters));
                setFilters(cleanedFilters);
                updateTotalFilters(cleanedFilters);

                setCriteria(prev => ({
                    ...prev,
                    registrationNumber: f.registrationNumber,
                    registrationDate: f.registrationDate,
                    segmentId: f.segmentId,
                    localIds: f.localIds,
                    localOperator: f.localOperator,
                    driverId: f.driverId,
                    useContractTypeId: f.useContractTypeId,
                    useContractActive: f.useContractActive,
                    useContractEndsAfter: f.useContractEndsAfter,
                    useContractEndsBefore: f.useContractEndsBefore,
                    inactives: f.inactives,
                    pathStartDate: f.pathStartDate,
                    pathEndDate: f.pathEndDate,
                    orderBy: f.orderBy,
                    orderColumn: f.orderColumn,
                    page: startPage,
                    itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
                }));

                if (f.orderBy && f.orderColumn) {
                    setOrder({
                        field: getFieldFromOrderColumn(f.orderColumn),
                        orderColumn: f.orderColumn,
                        isOrderAsc: f.orderBy === 'asc',
                    });
                }

                return;
            }

            // Normal load without scroll restoration
            f.scrollToItemId = undefined;
            f.page = 1;
            resetScrollState();
            localStorage.setItem(FILTERS_CACHE_KEY, JSON.stringify(f));

            onChangeFilters(f, true);
        } else {
            const f = {
                inactives: true,
            };

            onChangeFilters(f, true);
        }
    };

    const onChangeFilters = (f: Filters, isOnLoad?: boolean) => {
        updateTotalFilters(f);
        saveFiltersOnCache(f);

        // Reset items when filters change (for infinite scroll)
        if (!isOnLoad) {
            setVehicles([]);
            resetScrollState();
            setTotalItems(0);
        }

        // Use default order if not specified (when clearing filters)
        const orderColumn = f.orderColumn || 'v.registration_number';
        const orderBy = f.orderBy || 'asc';

        // Sync the order state for DataTable visual indicator
        setOrder({
            field: getFieldFromOrderColumn(orderColumn),
            orderColumn: orderColumn,
            isOrderAsc: orderBy === 'asc',
        });

        setCriteria(prev => ({
            ...prev,
            registrationNumber: f.registrationNumber,
            registrationDate: f.registrationDate,
            segmentId: f.segmentId,
            localIds: f.localIds,
            localOperator: f.localOperator,
            driverId: f.driverId,
            useContractTypeId: f.useContractTypeId,
            useContractActive: f.useContractActive,
            useContractEndsAfter: f.useContractEndsAfter,
            useContractEndsBefore: f.useContractEndsBefore,
            inactives: f.inactives,
            pathStartDate: f.pathStartDate,
            pathEndDate: f.pathEndDate,
            orderBy: orderBy,
            orderColumn: orderColumn,
            page: 1,
        }));
        setReRenderSearch(r => r + 1);
    };

    const updateTotalFilters = (f: Filters) => {
        let total = 0;

        if (f.registrationNumber) {
            total++;
        }

        if (f.registrationDate) {
            total++;
        }

        if (f.segmentId && f.segmentId.length > 0) {
            total++;
        }

        if (f.localIds?.length) {
            total++;
        }

        if (f.useContractTypeId) {
            total++;
        }

        if (f.useContractActive != undefined) {
            total++;
        }

        if (f.useContractEndsAfter || f.useContractEndsBefore) {
            total++;
        }

        if (f.inactives) {
            total++;
        }


        if (f.pathStartDate || f.pathEndDate) {
            total++;
        }

        setFiltersTotal(total);
    }

    const renderCounterBoxes = () => {
        return (
            <div className={compactPageLayoutStyles.rowCounters}>
                {counters && (
                    <InfoCard
                        label={t('vehicles_mileage.list.number_vehicles') as string}
                        value={counters.numberVehicles ?? 0}
                        icon={CarIcon}
                        backgroundColor={INFO_CARD_COLORS[0]}
                    />
                )}
                {counters && (
                    <InfoCard
                        label={t('vehicles_mileage.list.number_vehicles_exceeded') as string}
                        value={counters.numberVehiclesExceeded ?? 0}
                        icon={MediaIcon}
                        backgroundColor={INFO_CARD_COLORS[1]}
                    />
                )}
                {counters && (
                    <InfoCard
                        label={t('vehicles_mileage.list.kms_exceeded') as string}
                        value={`${formatNumber(counters.kmsExceeded ?? 0)} km`}
                        icon={AlertIcon}
                        backgroundColor={INFO_CARD_COLORS[2]}
                    />
                )}
            </div>
        );
    };

    // Build filter badge items for FilterBadgesOverflow
    const filterItems = useMemo<FilterBadgeItem[]>(() => {
        const items: FilterBadgeItem[] = [];

        // Matrícula
        if (filters.registrationNumber) {
            items.push({
                key: 'registrationNumber',
                label: t('vehicle.registration_identification') as string,
                value: filters.registrationNumber,
                onRemove: () => onChangeFilters({ ...filters, registrationNumber: undefined })
            });
        }

        // Segmento/Tipo
        if (filters.segmentName) {
            items.push({
                key: 'segment',
                label: t('vehicle.type') as string,
                value: filters.segmentName,
                onRemove: () => onChangeFilters({ ...filters, segmentId: undefined, segmentName: undefined })
            });
        }

        // Agência/Local
        if (filters.localNames?.length) {
            items.push({
                key: 'local',
                label: `${t('vehicle.local')} (${filters.localOperator === 'AND' ? t('common.filter_all' as any) : t('common.filter_any' as any)})` as string,
                value: filters.localNames.join(', '),
                onRemove: () => onChangeFilters({ ...filters, localIds: [], localNames: [] })
            });
        }

        // Condutor
        if (filters.driverName) {
            items.push({
                key: 'driver',
                label: t('common.state') as string,
                value: filters.driverName,
                onRemove: () => onChangeFilters({ ...filters, driverId: undefined, driverName: undefined })
            });
        }

        // Tipo financiamento
        if (filters.useContractTypeName) {
            items.push({
                key: 'useContractType',
                label: t('common.type_funding') as string,
                value: filters.useContractTypeName,
                onRemove: () => onChangeFilters({ ...filters, useContractTypeId: undefined, useContractTypeName: undefined })
            });
        }

        // Estado contrato
        if (filters.useContractActive !== undefined) {
            items.push({
                key: 'useContractActive',
                label: t('common.state') as string,
                value: filters.useContractActive ? t('contracts.states.in_progress') as string : t('contracts.states.expired') as string,
                onRemove: () => onChangeFilters({ ...filters, useContractActive: undefined })
            });
        }

        // Datas expiração contrato
        if (filters.useContractEndsBefore || filters.useContractEndsAfter) {
            const start = filters.useContractEndsAfter ? dayjs(filters.useContractEndsAfter).format(DATE_FORMAT_DEFAUT) : '';
            const end = filters.useContractEndsBefore ? dayjs(filters.useContractEndsBefore).format(DATE_FORMAT_DEFAUT) : '';
            items.push({
                key: 'contractExpiration',
                label: t('contracts.use_contract.expiration_date') as string,
                value: start && end ? `${t('common.range.from')} ${start} ${t('common.range.to')} ${end}` : start || end,
                onRemove: () => onChangeFilters({ ...filters, useContractEndsBefore: undefined, useContractEndsAfter: undefined })
            });
        }

        // Inativos
        if (filters.inactives) {
            items.push({
                key: 'inactives',
                label: t('vehicle.inactives') as string,
                value: t('common.yes') as string,
                onRemove: () => onChangeFilters({ ...filters, inactives: false })
            });
        }

        // Datas trajeto GPS
        if (filters.pathStartDate || filters.pathEndDate) {
            const start = filters.pathStartDate ? dayjs(filters.pathStartDate).format(DATE_FORMAT_DEFAUT) : '';
            const end = filters.pathEndDate ? dayjs(filters.pathEndDate).format(DATE_FORMAT_DEFAUT) : '';
            items.push({
                key: 'pathDates',
                label: t('vehicles_mileage.list.path_date') as string,
                value: start && end ? `${t('common.range.from')} ${start} ${t('common.range.to')} ${end}` : start || end,
                onRemove: () => onChangeFilters({ ...filters, pathStartDate: undefined, pathEndDate: undefined })
            });
        }

        return items;
    }, [filters, t]);

    const getVehiclesMileageReport = async () => {
        Loading.show();
        try {
            criteria.hasGpsReadPolicy = hasGpsKmsReadPolicy;
            const report = await VehiclesService.getVehiclesMileageReport(criteria);
            const title = t('menu.vehicles_mileage');
            saveAs(report, `${title}_${moment(new Date()).format('YYYY_MM_DD_HH_mm_ss')}.xlsx`);
            Loading.hide();
        } catch (error) {
            Loading.hide();
            Logger.error(LOGGER_LOG_TYPE.VEHICLES, `Couldn't get vehicles mileage report`, error);
            addToast(t('common.messages.error_load_info'), { appearance: 'error' });
        }
    }

    const fetchAllForExport = useCallback(async () => {
        const result = await VehiclesService.getList({
            ...criteria,
            page: 1,
            itemsPerPage: MAX_PAGINATION_ITEMS_PER_PAGE,
        });
        return result.items;
    }, [criteria]);

    return (<>
        <ScreenTitle title={t('vehicles_mileage.title')}>
            <CompactPageLayout
                title={t('vehicles_mileage.title') as string}
                contentAreaClassName={styles.contentArea}
                middleContent={<FilterBadgesOverflow items={filterItems} align="right" />}
                headerButtons={
                    <>
                        <Popover
                            contentContainerClassName={styles.filtersPopoverContainer}
                            containerStyle={{ boxShadow: '0 4px 14px rgba(0, 0, 0, 0.1)', backgroundColor: '#fff' }}
                            positions={['bottom', 'left']}
                            align={'end'}
                            onClickOutside={() => updateTotalFilters(filters)}
                            content={setIsPopoverOpen => (
                                <VehiclesMileageFiltersScreen
                                    filters={filters}
                                    onFilter={f => { onChangeFilters(f); setIsPopoverOpen(false); }}
                                    onChange={f => updateTotalFilters(f)}
                                    showTrajectDatesFilter={Boolean(hasGpsKmsReadPolicy)}
                                />
                            )}
                        >
                            {(isPopoverOpen, setIsPopoverOpen) => (
                                <ScreenHeaderButton icon={FiltersIcon} onClick={() => setIsPopoverOpen(!isPopoverOpen)}>
                                    {filtersTotal > 0 && (
                                        <div className={styles.counterList}>
                                            <div className={styles.counterNumber}>{filtersTotal}</div>
                                        </div>
                                    )}
                                </ScreenHeaderButton>
                            )}
                        </Popover>

                        <Dropdown
                            style={{ right: '10px' }}
                            options={
                                <>
                                    <DropdownItem onClick={() => getVehiclesMileageReport()}>
                                        {t('vehicles_mileage.list.export_excel')}
                                    </DropdownItem>
                                    {hasExportListingPolicy && (
                                        <DropdownItem onClick={() => exportListingRef.current?.exportExcel()}>
                                            {t('common.export_listing')}
                                        </DropdownItem>
                                    )}
                                </>
                            }
                        >
                            <div className={styles.exportButton}>
                                <FaFileExport size={20} color={colors.primaryDark} />
                            </div>
                        </Dropdown>
                    </>
                }
            >
                {counters && (
                    <div className={compactPageLayoutStyles.countersSection}>
                        {renderCounterBoxes()}
                    </div>
                )}

                <div className={compactPageLayoutStyles.listTblWrapper}>
                    {isFirstLoad.current === false && (
                        <DataTableWithSidebar
                            key={reRenderSearch}
                            cacheKey="VEHICLESMILEAGE_TABLE"
                            columns={tableColumns}
                            defaultHiddenColumns={['vehicleSegmentName', 'vehicleLocals']}
                            rows={vehicles}
                            defaultOrder={order}
                            onRowClick={(row, index, event) => onRowClick(row, index, event)}
                            onOrderChange={handleOrderChange}
                            onHref={row => `/vehicles/details/${row.id}`}
                            onRowId={row => row.id ?? ''}
                            scrollToRowId={scrollToItemId}
                            infiniteScroll={{
                                isLoading: isLoadingMore,
                                hasMore: getHasMore(vehicles.length, totalItems),
                                onLoadMore: getOnLoadMore(loadMore),
                                hasMorePrevious: getHasMorePrevious(),
                                isLoadingPrevious: isLoadingPrevious,
                                onLoadPrevious: getOnLoadPrevious(loadPrevious),
                            }}
                            exportExcel={{
                                fetchAll: fetchAllForExport,
                                fileName: t('vehicles_mileage.title') as string,
                            }}
                            exportRef={exportListingRef}
                            hideExportInSidebar={true}
                        />
                    )}
                </div>
            </CompactPageLayout>

            <ReactTooltip
                id="mileage-tooltip"
                className="centered-tooltip"
                getContent={dataTip => {
                    if (!dataTip) return null;
                    return <span dangerouslySetInnerHTML={{ __html: dataTip }} />;
                }}
            />
        </ScreenTitle>
        </>
    );
}

export default VehiclesMileageScreen;
